import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Link as LinkIcon } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  FormField,
  Select,
} from "@/components/ui";
import {
  CUSTOM_EXPIRY_VALUE,
  DEFAULT_EXPIRY_MINUTES,
  EXPIRY_PRESETS,
  MAX_CUSTOM_EXPIRY_DAYS,
  daysToMinutes,
  expiryLabelForMinutes,
} from "@/features/docs/constants/expiry";
// NB: we use raw fetch here (not apiGet/apiPost). The /api/docs/* endpoints
// return Pydantic models directly instead of the {status,data,error,meta}
// envelope that apiClient expects — going through apiClient would return
// undefined for the payload. See ADR-45cb51db.

/**
 * Internal admin UI for generating library-wide magic links for the docs portal.
 *
 * Route: `/docs/admin/magic-links` — Easy Auth (any Entra-authenticated
 * internal user). External magic-link-authenticated users cannot reach it
 * because the route is not wrapped in DocsAuthProvider and does not accept
 * JWT auth; the backend endpoint `/api/docs/magic-link` further enforces
 * the `docs.admin` role.
 *
 * Generates scope=all tokens. For folder- or page-scoped links use the
 * contextual share buttons in the sidebar and page header (ADR-71391314).
 */

const EXPIRY_OPTIONS = [
  ...EXPIRY_PRESETS.map((p) => ({ value: String(p.value), label: p.label })),
  { value: CUSTOM_EXPIRY_VALUE, label: "Custom…" },
];

const DEFAULT_EXPIRY = String(DEFAULT_EXPIRY_MINUTES);
const MAX_RECENT = 5;

interface InventoryPage {
  id: string;
  title: string;
}

interface MagicLinkResponse {
  magic_link_url: string;
  customer_name: string;
  scope: string;
  expires_in_minutes: number;
  page_id: string | null;
}

interface RecentLink {
  url: string;
  customerName: string;
  scope: string;
  expiresInMinutes: number;
  pageTitle: string | null;
  generatedAt: number;
}

function toFullUrl(path: string): string {
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}


export function MagicLinkAdminPage() {
  const [customerName, setCustomerName] = useState("");
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  const [customDays, setCustomDays] = useState("");
  const [pageId, setPageId] = useState("");
  const [inventory, setInventory] = useState<InventoryPage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ customerName?: string; customDays?: string }>({});
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [recent, setRecent] = useState<RecentLink[]>([]);

  // Fetch Confluence page inventory for the optional target-page dropdown.
  // Uses Easy Auth cookies — no JWT needed for internal users.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/docs/inventory", {
          credentials: "include",
        });
        if (!response.ok) return;
        const data = (await response.json()) as { pages?: InventoryPage[] };
        if (!cancelled && Array.isArray(data.pages)) {
          setInventory(data.pages.map((p) => ({ id: p.id, title: p.title })));
        }
      } catch {
        // Inventory is optional — silently skip if unreachable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pageOptions = useMemo(
    () => [
      { value: "", label: "Library landing (no target page)" },
      ...inventory.map((p) => ({ value: p.id, label: p.title })),
    ],
    [inventory],
  );

  function resolveExpiryMinutes(): number | null {
    if (expiry !== CUSTOM_EXPIRY_VALUE) return Number(expiry);
    const days = Number(customDays);
    if (!Number.isFinite(days) || days <= 0) return null;
    if (days > MAX_CUSTOM_EXPIRY_DAYS) return null;
    return daysToMinutes(days);
  }

  function validate(): boolean {
    const errors: typeof fieldErrors = {};
    if (!customerName.trim()) {
      errors.customerName = "Customer name is required";
    }
    if (expiry === CUSTOM_EXPIRY_VALUE) {
      const days = Number(customDays);
      if (!customDays.trim() || !Number.isFinite(days) || days <= 0) {
        errors.customDays = "Enter a number of days greater than 0";
      } else if (days > MAX_CUSTOM_EXPIRY_DAYS) {
        errors.customDays = `Maximum is ${MAX_CUSTOM_EXPIRY_DAYS} days`;
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setGeneratedUrl(null);
    setCopied(false);
    if (!validate()) return;

    const minutes = resolveExpiryMinutes();
    if (minutes === null) return;

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        customer_name: customerName.trim(),
        scope: "all",
        expires_in_minutes: minutes,
      };
      if (pageId) body.page_id = pageId;

      const response = await fetch("/api/docs/magic-link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let detail = `Request failed with status ${response.status}`;
        try {
          const errBody = await response.json();
          if (typeof errBody?.detail === "string") detail = errBody.detail;
        } catch {
          // body was not JSON
        }
        throw new Error(detail);
      }
      const data = (await response.json()) as MagicLinkResponse;
      const fullUrl = toFullUrl(data.magic_link_url);
      setGeneratedUrl(fullUrl);

      const pageTitle = data.page_id
        ? (inventory.find((p) => p.id === data.page_id)?.title ?? data.page_id)
        : null;

      setRecent((prev) => [
        {
          url: fullUrl,
          customerName: data.customer_name,
          scope: data.scope,
          expiresInMinutes: data.expires_in_minutes,
          pageTitle,
          generatedAt: Date.now(),
        },
        ...prev,
      ].slice(0, MAX_RECENT));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to generate magic link");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyGenerated() {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFormError("Unable to copy to clipboard — copy the link manually");
    }
  }

  async function copyRecent(url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Silently ignore — user can long-click the link.
    }
  }

  function resetForGenerate() {
    setGeneratedUrl(null);
    setCopied(false);
    setFormError(null);
  }

  return (
    <main className="min-h-screen bg-ev-surface-subtle px-6 py-10">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-ev-text-primary">Docs Magic Links</h1>
          <p className="text-sm text-ev-text-secondary">
            Generate a full-library magic link for an Implementation customer and paste the
            URL into their OnRamp task. For folder- or page-scoped links, use the Share
            buttons in the sidebar and page header.
          </p>
        </header>

        <section className="rounded-ev border border-ev-border bg-ev-surface p-6">
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <FormField
              label="Customer name"
              type="text"
              value={customerName}
              onChange={(e) => {
                setCustomerName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, customerName: undefined }));
              }}
              placeholder="Plan Health Co"
              helpText="Appears in the docs portal header and PDF branding."
              error={fieldErrors.customerName}
              required
            />

            <Select
              label="Expiry"
              value={expiry}
              onChange={(v) => {
                setExpiry(v);
                setFieldErrors((prev) => ({ ...prev, customDays: undefined }));
              }}
              options={EXPIRY_OPTIONS}
            />

            {expiry === CUSTOM_EXPIRY_VALUE && (
              <FormField
                label="Custom expiry (days)"
                type="number"
                min={1}
                max={MAX_CUSTOM_EXPIRY_DAYS}
                step={1}
                value={customDays}
                onChange={(e) => {
                  setCustomDays(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, customDays: undefined }));
                }}
                placeholder="e.g. 45"
                helpText={`Maximum ${MAX_CUSTOM_EXPIRY_DAYS} days.`}
                error={fieldErrors.customDays}
                required
              />
            )}

            <Select
              label="Target page (optional)"
              value={pageId}
              onChange={setPageId}
              options={pageOptions}
              helpText="Deep-link the customer to a specific explainer. Leave on library landing for the default index."
            />

            {formError && <Alert variant="destructive" message={formError} />}

            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                onClick={resetForGenerate}
              >
                <LinkIcon className="h-4 w-4" aria-hidden="true" />
                Generate link
              </Button>
            </div>
          </form>
        </section>

        {generatedUrl && (
          <section
            aria-label="Generated magic link"
            className="rounded-ev border border-ev-success-border bg-ev-success-bg p-5"
          >
            <p className="mb-2 text-sm font-medium text-ev-success-text">
              Magic link generated
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={generatedUrl}
                readOnly
                aria-label="Magic link URL"
                className="flex-1 rounded-ev border border-ev-border bg-ev-surface px-3 py-2 text-sm text-ev-text-primary"
              />
              <Button variant="outline" onClick={copyGenerated} type="button">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-ev-success-text" aria-hidden="true" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </section>
        )}

        {recent.length > 0 && (
          <section className="rounded-ev border border-ev-border bg-ev-surface p-6">
            <h2 className="mb-3 text-sm font-semibold text-ev-text-primary">
              Recent links (this session)
            </h2>
            <ul className="space-y-3">
              {recent.map((link) => (
                <li
                  key={link.generatedAt}
                  className="rounded-ev border border-ev-border bg-ev-surface-subtle p-3"
                >
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-ev-text-primary">{link.customerName}</span>
                    <Badge variant="neutral">{expiryLabelForMinutes(link.expiresInMinutes)}</Badge>
                    {link.pageTitle && <Badge variant="info">{link.pageTitle}</Badge>}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={link.url}
                      readOnly
                      aria-label={`Magic link URL for ${link.customerName}`}
                      className="flex-1 rounded border border-ev-border bg-ev-surface px-2 py-1 text-xs text-ev-text-primary"
                    />
                    <Button variant="ghost" size="sm" type="button" onClick={() => copyRecent(link.url)}>
                      <Copy className="h-4 w-4" aria-hidden="true" />
                      Copy
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
